<template>
  <v-dialog 
    v-model="activeMode"  
    max-width="400"
    @click:outside="closeForm()"
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <template v-slot:activator="{ on }">
      <v-btn v-if="value" icon v-on="on">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn v-else color="primary" dark v-on="on"> 
        <v-icon dark>mdi-plus</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-toolbar
        color="primary"
        dark
        flat
      >
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeForm()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="mt-7">
        <v-form ref="form" v-model="isValidForm">
          <v-card>
            <v-card-title>Обязательные параметры</v-card-title>
            <v-card-text>
              <v-text-field
                v-model="entity.name"
                label="Название комнаты *"
                filled
                :rules="[v => !!v || 'Введите название комнаты', v => !checkClone() || 'Уже есть комната с таким именем.']"
              ></v-text-field>
              <v-select
                v-model="entity.roomType"
                label="Тип комнаты *"
                filled
                :items="roomTypes"
                :rules="[v => !!v || 'Выберите тип комнаты']"
              ></v-select>
              <v-text-field
                v-model="entity.finalParams.floor_square"
                label="Площадь пола *"
                filled
                suffix="м²"
                :rules="[v => !!v || 'Укажите площадь комнаты.', v => (isNumber(v) && v > 0) || 'Введите число больше 0.']"
              ></v-text-field>
              <v-text-field
                v-model="entity.height"
                label="Высота потолка *"
                filled
                suffix="м"
                :rules="[v => !!v || 'Укажите высоту потолка.', v => isNumber(v) || 'Введите число.']"
              ></v-text-field>
            </v-card-text>
          </v-card>

          <v-card class="mt-5">
            <v-card-title>Дополнительные параметры</v-card-title>
            <v-card-text>
              <v-alert type="warning" outlined dense>
                <small>Мы автоматически рассчитали примерные значения параметров на основе типа комнаты, ее площади и высоты потолка. 
                Для большей точности сметы вы можете скорректировать эти данные вручную.</small>
              </v-alert>
              <v-text-field
                v-for="item of fields"
                :key="item.tag"
                v-model="entity.finalParams[item.tag]"
                :label="item.label"
                :suffix="item.suffix"
                filled
                :rules="[v => isNumber(v) || 'Введите число']"
              ></v-text-field>
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeForm()">Закрыть</v-btn>
        <v-btn 
          color="blue darken-1" 
          text 
          @click="save"
        >Сохранить</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'room-card',

  props: {
    value: Object,
    defaultHeight: Number,
    rooms: Array
  },
  
  data() {
    return {
      activeMode: false,
      isValidForm: false,

      title: this.value ? 'Карточка комнаты' : 'Создание комнаты',

      entity: this.value ? _.cloneDeep(this.value) : this.getDefaultObj(),
      roomTypes: [
        'Санузел',
        'Коридор',
        'Кухня',
        'Жилая комната',
        'Нежилая комната',
        'Балкон (лоджия)'
      ],
      fields: [
        {
          tag: 'walls_square',
          label: 'Площадь стен',
          suffix: 'м2'
        },
        {
          tag: 'ceiling_square',
          label: 'Площадь потолка',
          suffix: 'м2'
        },
        {
          tag: 'floor_perimeter',
          label: 'Периметр пола',
          suffix: 'м'
        },
        {
          tag: 'ceiling_perimeter',
          label: 'Периметр потолка',
          suffix: 'м'
        },
        {
          tag: 'slopes_length',
          label: 'Длина откосов (под отделку)',
          suffix: 'м'
        },
        {
          tag: 'windowsill_length',
          label: 'Длина подоконников',
          suffix: 'м'
        },
        {
          tag: 'electric_points_count',
          label: 'Число электроточек',
          suffix: 'шт'
        },
        {
          tag: 'water_points_count',
          label: 'Число сантехнических точек',
          suffix: 'шт'
        },
      ]
    };
  },

  watch: {
    ['entity.height']() {
      this.updateParams();
    },
    ['entity.finalParams.floor_square']() {
      this.updateParams();
    },
    ['entity.roomType']() {
      this.updateParams();
    },
  },

  methods: {
    getDefaultObj() {
      return {
        name: '',
        roomType: '', // Тип комнаты - С/у, кухня, жилая комната, коридор и т.д.
        height: this.isNumber(this.defaultHeight) ? this.defaultHeight : 3,
        finalParams: {
          floor_square: 0,
          walls_square: 0,
          ceiling_square: 0,
          floor_perimeter: 0,
          ceiling_perimeter: 0,
          slopes_length: 0,
          windowsill_length: 0,
          electric_points_count: 0,
          water_points_count: 0
        }
      }
    },

    updateParams() {
      let height = this.entity.height;
      let square = this.entity.finalParams.floor_square;
      let roomType = this.entity.roomType;
      if (!this.isNumber(height) || !this.isNumber(square) || !roomType || height <= 0 || square <=0) return;
      height = Number(height);
      square = Number(square);
      
      // Вычисляем параметры на основе введенных данных
      // Базовые параметры (комната = квадрат)
      let baseWallSquare = Math.sqrt(square) * 4 * height;
      let basePerimeter = Math.sqrt(square) * 4.
      // Для всех комнат - вычитаем 1 дверь
      let realWallSquare = baseWallSquare - 0.9 * 2.1;
      let realPerimeter = basePerimeter;
      let realSlopesLength = 0;
      let realWindowsillLength = 0;
      let realElectricPoints = Math.floor(square / 4 + 1); // 1 точка на 4м2 + 1 точка на помещение
      let realWaterPoints = 0;
      // Корректируем данные в зависимости от типа комнаты
      switch (roomType) {
        case 'Санузел':
          /*
            Конфигурация по воде:
            - 3 точки - раковина
            - 1 точка - унитаз
            - 3 точки - ванная
            Электрика: 1 розетка + 1 выключатель.
          */
          realWaterPoints = 7;
          realElectricPoints = 2;
          break;
        case 'Коридор':

          break;
        case 'Кухня':
          // 1 раковина = 3 точки
          realWaterPoints = 3;
          break;
        case 'Жилая комната':
          // 1 окно - 2x2 = 4м2 + подоконник 2м
          realWallSquare -= 4;
          realSlopesLength = 6;
          realWindowsillLength = 2;
          break;
        case 'Нежилая комната':
          // 1 электроточка
          realElectricPoints = 1;
          break;
      }
      this.entity.finalParams.walls_square = realWallSquare.toFixed(2);
      this.entity.finalParams.ceiling_square = square.toFixed(2);
      this.entity.finalParams.floor_perimeter = realPerimeter.toFixed(2);
      this.entity.finalParams.ceiling_perimeter = realPerimeter.toFixed(2);
      this.entity.finalParams.slopes_length = realSlopesLength.toFixed(2);
      this.entity.finalParams.windowsill_length = realWindowsillLength.toFixed(2);
      this.entity.finalParams.electric_points_count = realElectricPoints.toFixed(0);
      this.entity.finalParams.water_points_count = realWaterPoints.toFixed(0);
    },

    save() {
      this.$refs.form.validate();

      if (this.isValidForm) {
        this.$emit('save', this.entity);
        this.$nextTick(() => this.closeForm());
      }
    },

    closeForm() {
      this.scrollUp();
      this.activeMode = false;
      this.entity = this.value ? _.cloneDeep(this.value) : this.getDefaultObj();
      this.$refs.form.resetValidation();
    },

    checkClone() {
      // Проверка уникальности имени
      let clone = this.rooms.find(x => (!this.entity._id || String(x._id) != this.entity._id) && x.name == this.entity.name);
      return Boolean(clone);
    }
  },

  computed: {
  
  }
}
</script>